import classNames from "classnames";
import { CheckIcon } from "@heroicons/react/20/solid";
import { Combobox, Transition, Portal } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState, forwardRef } from "react";
import type { CSSProperties, UIEvent } from "react";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";

import { debounce } from "../../../shared/utils/DateUtil";
import {
  DropdownWithSearchOption,
  DropdownWithSearchParams
} from "./interface";
import { InfoCircle } from "@untitled-ui/icons-react";
import { whiteLabelStyling } from "../../../shared/utils/constants";

const UICombobox: any = Combobox;

function MultipleCheckbox(
  { selected = false, active = false },
  whiteLabelStyle: any
) {
  return (
    <div
      className={classNames(
        "flex items-center justify-center border h-4 w-[21px] rounded-sm",
        selected
          ? `${whiteLabelStyle?.theme?.color?.border || "border-indigo-600"}`
          : "border-gray-200",
        active ? "border-white" : ""
      )}
    >
      {selected ? (
        <span
          className={`w-4 h-4 rounded-sm flex items-center justify-center ${
            active
              ? ""
              : `text-white ${
                  whiteLabelStyle?.theme?.color?.darkbg || "bg-indigo-600"
                }`
          }`}
        >
          <CheckIcon className="h-3 w-3" aria-hidden="true" />
        </span>
      ) : null}
    </div>
  );
}

function SingleCheckmark({ selected = false, active = false }) {
  return (
    <div
      className={classNames(
        "flex items-center justify-center w-5 h-5",
        selected ? "" : "",
        active ? "" : ""
      )}
    >
      {selected ? (
        <span
          className={`w-5 h-5 flex items-center justify-center ${
            active ? "text-white" : "text-indigo"
          }`}
        >
          <CheckIcon className="h-4 w-4" aria-hidden="true" />
        </span>
      ) : null}
    </div>
  );
}

const DropdownWithSearch = forwardRef<HTMLDivElement, DropdownWithSearchParams>(
  (
    {
      searchFunc,
      options,
      placeholder,
      multiple,
      onChange,
      styles = {
        container: "",
        containerWidth: false,
        label: "",
        dropdown: "",
        button: "",
        chevronIcon: "",
        buttonContainer: "",
        dropdownItem: ""
      },
      style,
      label = "",
      mode = "",
      selectedItems,
      emptyText = "No results found",
      hasSearch = true,
      disabled = false,
      requiredIcon = false,
      hasSelectAll = false,
      inputPlaceholder = "",
      showDropdownIndicator = true,
      onClick,
      infoIcon = false,
      infoTooltip = "",
      errorText = "",
      whiteLabelStyle: _whiteLabelStyleProps = {},
      usePortal = false,
      portalZIndex = 1000,
      truncateName = false,
      onLoadMore,
      hasMore = false
    },
    ref
  ) => {
    const { whiteLabelStyle } = whiteLabelStyling();
    const wl: any = whiteLabelStyle as any;
    void _whiteLabelStyleProps;
    const [selected, setSelected] = useState<
      NonNullable<DropdownWithSearchParams["selectedItems"]>
    >(selectedItems || (multiple ? [] : { name: "", value: "" }));
    const [query, setQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isSearched, setIsSearched] = useState(false);
    const [selectAllOption, setSelectAllOptions] = useState(false);
    const [panelStyle, setPanelStyle] = useState<CSSProperties>({});

    const [isOpen, setIsOpen] = useState(false);

    const estimatePanelHeight = () => {
      const ITEM_HEIGHT = 32; // py-2
      const SEARCH_HEIGHT = hasSearch ? 40 : 0;
      const VERTICAL_PADDING = 16; // py-2 container
      const maxHeight = 240; // Tailwind max-h-60 (15rem)
      const itemsHeight =
        Math.min(
          options?.length ?? 0,
          Math.floor((maxHeight - SEARCH_HEIGHT) / ITEM_HEIGHT)
        ) * ITEM_HEIGHT;

      return Math.min(
        maxHeight,
        itemsHeight + SEARCH_HEIGHT + VERTICAL_PADDING
      );
    };

    const buttonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
      if (selectedItems && (!(selected as any) || !(selected as any)?.length)) {
        setSelected(selectedItems);
      }
    }, [selectedItems]);

    useEffect(() => {
      if (
        multiple &&
        Array.isArray(selectedItems) &&
        selectedItems?.length === options?.length
      ) {
        setSelectAllOptions(true);
      } else {
        setSelectAllOptions(false);
      }
    }, [selectedItems, options, multiple]);

    // Keep panel aligned on scroll/resize while open
    useEffect(() => {
      if (!usePortal || !isOpen) return;

      let rafId: number | null = null;
      const onScrollOrResize = () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => computePanelPosition());
      };

      window.addEventListener("scroll", onScrollOrResize, true);
      window.addEventListener("resize", onScrollOrResize, true);

      return () => {
        if (rafId !== null) cancelAnimationFrame(rafId);

        window.removeEventListener("scroll", onScrollOrResize, true);
        window.removeEventListener("resize", onScrollOrResize, true);
      };
    }, [usePortal, isOpen, hasSearch, options?.length]);

    const computePanelPosition = () => {
      if (!usePortal || !buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();
      const panelHeight = estimatePanelHeight();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < panelHeight + 8 && rect.top > panelHeight + 8;

      const top = openUp ? rect.top - panelHeight - 8 : rect.bottom + 8;

      setPanelStyle({
        position: "fixed",
        top,
        left: rect.left,
        width: rect.width,
        zIndex: portalZIndex
      });
    };

    function onOptionSelect(
      data: DropdownWithSearchOption | Array<DropdownWithSearchOption>
    ) {
      setSelected(data);
      onChange(data);
    }

    function comparator(
      a: DropdownWithSearchOption,
      b: DropdownWithSearchOption
    ) {
      return a.value === b.value;
    }

    let multipleButtonText = "";
    if (multiple) {
      const selectedArray = Array.isArray(selected) ? selected : [];
      if (selectedArray.length > 0) {
        multipleButtonText = (selectedArray[0] as any).name;
      }
      const additionalCount =
        selectedArray.length > 1 ? selectedArray.length - 1 : 0;
      if (additionalCount) {
        multipleButtonText += " + " + additionalCount + " item(s)";
      }
    }

    const selectAllHandler = (check: boolean) => {
      if (!multiple) return;

      const selectedArray = Array.isArray(selected) ? selected : [];
      const notSelectedOptions = [] as any[];
      options?.forEach((option: any) => {
        const isExist = selectedArray?.some(
          (item: any) => item?.value === option?.value
        );
        if (!isExist) {
          notSelectedOptions?.push(option);
        }
      });

      const selectedOptions = check
        ? [...selectedArray, ...notSelectedOptions]
        : [];
      setSelected(selectedOptions as any);
      onChange?.(selectedOptions);
      setSelectAllOptions(check);
    };

    const handleSearchChange = debounce(async (text: string) => {
      setIsLoading(true);
      await searchFunc?.(text);
      setIsLoading(false);
      if (text?.length === 0) {
        setIsSearched(false);
      } else {
        setIsSearched(true);
      }

      setQuery(text);
    }, 300);

    const handleOptionsScroll = async (e: UIEvent<HTMLUListElement>) => {
      if (!onLoadMore || !hasMore || isLoadingMore || isLoading) return;

      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const nearBottom = scrollHeight - scrollTop - clientHeight < 48;
      if (!nearBottom) return;

      setIsLoadingMore(true);
      await onLoadMore();
      setIsLoadingMore(false);
    };

    // Render dropdown panel content (shared between portal and non-portal modes)
    const renderDropdownPanel = () => (
      <Transition
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        afterLeave={() => {
          setQuery("");
          setIsOpen(false);
        }}
      >
        <div
          className={classNames(
            "h-auto shadow-lg bg-white border border-gray-50 rounded-md py-2 w-full min-w-0",
            styles.dropdown,
            !usePortal && mode === "reverse"
              ? "flex flex-col bottom-[40px]"
              : "",
            !usePortal ? "absolute mt-1 z-[1000]" : ""
          )}
          style={usePortal ? panelStyle : undefined}
        >
          {hasSearch && (
            <Combobox.Input
              tabIndex={usePortal ? undefined : 0}
              className={classNames(
                "block w-[calc(100%_-_16px)] border border-gray-200 mx-2 rounded-lg py-2 px-2 text-xs leading-4 text-gray-900 truncate outline-none mb-1",
                mode === "reverse" ? "order-2" : "order-1"
              )}
              displayValue={() => query}
              onChange={(e) => {
                e.preventDefault();
                if (
                  e.target.value?.length > 0 &&
                  /^\s*$/g.test(e.target.value)
                ) {
                  e.target.value = e.target.value.slice(0, 0);
                  setQuery(e.target.value);
                  return;
                }
                handleSearchChange(e.target.value);
              }}
              placeholder={inputPlaceholder}
            />
          )}

          <Combobox.Options
            className={classNames(
              "w-full py-1 text-xs focus:outline-none overflow-auto max-h-60 bg-white",
              mode === "reverse" ? "order-1" : "order-2"
            )}
            style={usePortal ? { overflowX: "auto" } : undefined}
            onClick={!usePortal ? (e) => e.stopPropagation() : undefined}
            onScroll={onLoadMore ? handleOptionsScroll : undefined}
          >
            {options?.length === 0 ? (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                {isLoading ? null : isSearched ? "No results found" : emptyText}
              </div>
            ) : (
              <>
                {hasSelectAll && multiple && (
                  <label
                    className={classNames(
                      "inline-flex items-center hover:text-white w-full px-2 py-2",
                      wl?.theme?.hover?.darkbg1 || "hover:bg-indigo-600"
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selectAllOption}
                      onChange={(e) => selectAllHandler(e?.target?.checked)}
                      className={classNames(
                        "form-checkbox h-4 w-4 transition duration-150 ease-in-out border-gray-200 rounded-[2px] ml-2 focus:ring-0 focus:ring-transparent",
                        wl?.theme?.color?.text || "text-indigo-600"
                      )}
                    />
                    <span className="ml-2 text-inherit text-xs">
                      Select All
                    </span>
                  </label>
                )}
                {options?.map((option, optionIdx) => (
                  <Combobox.Option
                    key={optionIdx}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 px-4 ${
                        active
                          ? `${wl?.theme?.color?.bg || "bg-indigo-600"} text-white`
                          : "text-gray-900"
                      } ${styles?.dropdownItem}`
                    }
                    value={option}
                  >
                    {({ selected, active }) => (
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center justify-start">
                          {multiple && (
                            <MultipleCheckbox
                              selected={selected}
                              active={active}
                            />
                          )}
                          <span
                            className={`block w-full pl-2 text-left whitespace-nowrap overflow-hidden text-ellipsis ${
                              selected ? "font-normal" : "font-normal"
                            }`}
                            title={
                              typeof option.name === "string" ? option.name : ""
                            }
                          >
                            {option.name}
                          </span>
                        </div>
                        {!multiple && (
                          <div>
                            <SingleCheckmark
                              active={active}
                              selected={selected}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </Combobox.Option>
                ))}

                {isLoadingMore && (
                  <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                    Loading...
                  </div>
                )}
              </>
            )}
          </Combobox.Options>
        </div>
      </Transition>
    );

    return (
      <div
        ref={ref}
        className={classNames(
          styles?.container,
          styles?.containerWidth || "w-full min-w-0"
        )}
      >
        <UICombobox
          value={selected as any}
          onChange={onOptionSelect as any}
          multiple={multiple}
          by={comparator}
          disabled={disabled}
        >
          <div className="relative">
            {label && (
              <Combobox.Label
                className={classNames(
                  "block font-medium text-gray-700 text-xs",
                  styles.label || "mb-2"
                )}
              >
                {label}{" "}
                {requiredIcon && (
                  <span className="text-inherit text-red-700 ml-1">*</span>
                )}
                {infoIcon && (
                  <div
                    className="inline-block items-center text-inherit cursor-pointer"
                    title={infoTooltip}
                  >
                    <InfoCircle className="w-4 h-4" />
                  </div>
                )}
              </Combobox.Label>
            )}

            <div
              className={classNames(
                "relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left",
                styles.buttonContainer ?? ""
              )}
            >
              <Combobox.Button
                ref={buttonRef}
                className={classNames(
                  "rounded-lg w-full border border-gray-200 flex items-center justify-between py-2 px-3 sm:px-4 text-xs text-gray-700 min-h-[40px]",
                  styles.button ?? "",
                  disabled ? "bg-gray-100" : "",
                  errorText !== "" ? "border-red-700" : "border-gray-200"
                )}
                style={style?.button ?? {}}
                title={
                  multiple && Array.isArray(selected)
                    ? (selected as any[]).length > 0 &&
                      (selected as any[]).map((s: any) => s.name).join(",")
                    : (selected as any).name
                }
                onClick={async () => {
                  if (hasSearch) {
                    setIsLoading(true);
                    await searchFunc?.("");
                    setIsSearched(false);
                    setIsLoading(false);
                  }

                  onClick?.();
                  setIsOpen(true);
                  computePanelPosition();
                }}
              >
                {multiple ? (
                  Array.isArray(selected) && (selected as any[]).length > 0 ? (
                    <ul className="h-full flex pr-10 text-left min-w-0">
                      <span className="truncate flex-1">
                        {multipleButtonText}
                      </span>
                    </ul>
                  ) : (
                    <span className="text-gray-500 min-w-0">{placeholder}</span>
                  )
                ) : (
                  <span
                    className={classNames(
                      "text-left min-w-0 flex-1",
                      truncateName ? "truncate" : ""
                    )}
                  >
                    {(selected as any).name || (
                      <span className="text-gray-500">{placeholder}</span>
                    )}
                  </span>
                )}

                {showDropdownIndicator && (
                  <ChevronUpDownIcon
                    aria-hidden="true"
                    className={classNames(
                      "h-5 w-5 text-gray-400",
                      styles.chevronIcon ?? ""
                    )}
                  />
                )}
              </Combobox.Button>
            </div>

            {usePortal ? (
              <Portal>{renderDropdownPanel()}</Portal>
            ) : (
              renderDropdownPanel()
            )}
          </div>
        </UICombobox>

        {errorText && (
          <p className="mt-1 pl-2 text-xs text-red-700">{errorText}</p>
        )}
      </div>
    );
  }
);

DropdownWithSearch.displayName = "DropdownWithSearch";

export default DropdownWithSearch;
